import React, {useMemo} from 'react';
import {Box, Button, Grid} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {useAccountContext} from '../../../contexts/AccountContext';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {transferFieldsType} from './builderTransferFields';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {OVRecommendActionEnum, BusinessUnitEnum} from '../../../schema-types/globalTypes';
import {
  transferSourcePots,
  transferShellPlans,
  transferDestinationPots,
} from './BuilderTransferHelpers';
import {BuilderTransferShellPlanForm} from './BuilderTransferSourcePlanForm';
import {TransferSourceParent} from './TransferSourceParent';
import {TransferDestinationParent} from './TransferDestinationParent';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {reccNonEditable} from '../CreateBuilder/BuilderSourceDestination/CreateBuilderFooter';
import {CASH_OBJ} from '../../../utils/potsFilter';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {
  changeTransferDestination,
  resetTransferSourcesOnPlanTypeChange,
  resetDestinationIfInSources,
  addShellSourcePlan,
  updateTransferSourceIdFactory,
  useResetTransferSourcesOnPlanTypeChange,
  useResetDestinationIfInSources,
  useDeriveCombinedSingleAmount,
} from '../hooks/builderTransferHooks';
import {useTransferPotsQuery} from './useTransferPotsQuery';

export {
  changeTransferDestination,
  resetTransferSourcesOnPlanTypeChange,
  resetDestinationIfInSources,
  addShellSourcePlan,
  useResetTransferSourcesOnPlanTypeChange,
  useResetDestinationIfInSources,
};

export const Transfer: React.FC<BuilderProps> = ({sourceDestinationLabels}) => {
  const {sourcePots} = useTransferPotsQuery();
  const {accountType} = useAccountContext();
  const {builderDetails, builderReviewId} = useBuilderContext();
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const isNonEditable = !!reccNonEditable(builderDetails);

  const [
    transferDestinationFieldObj,
    transferDestinationFieldMeta,
    transferDestinationFieldHelpers,
  ] = useAutoField<transferFieldsType['destinationValues']>('transfer.destinationValues');
  const [transferSourcesFieldObj, transferSourcesFieldMeta, transferSourcesFieldHelpers] =
    useAutoField<transferFieldsType['transferSources'] | undefined>(
      'transfer.transferSources'
    );
  const [, , combinedSingleAmountHelpers] = useAutoField<
    transferFieldsType['combinedSingleAmount']
  >('transfer.combinedSingleAmount');

  useDeriveCombinedSingleAmount({
    transferSources: transferSourcesFieldObj.value,
    combinedSingleAmountHelpers,
  });

  useResetBuilder(OVRecommendActionEnum.TRANSFER);
  const {mainPlanType} = values;
  const {shellProducts} = usePlanNamesProviders();

  const checkedListSources: {
    normalSources: string[];
    shellPlans: string[];
    total: string[];
  } = useMemo(() => {
    if (!transferSourcesFieldObj.value) {
      return {normalSources: [], shellPlans: [], total: []};
    }
    const total = Object.keys(transferSourcesFieldObj.value || {});
    const normalSources = total.filter((x) => !x.includes('shell'));
    const shellPlans = total.filter((x) => x.includes('shell'));
    return {normalSources, total, shellPlans};
  }, [transferSourcesFieldObj.value]);

  const destinationExistingPotId =
    transferDestinationFieldObj.value?.existingPotId ?? null;
  const destinationParentPotId = transferDestinationFieldObj.value?.parentPotId;

  const changeDestination = (pot: BuilderPot | BuilderPot_dentistChildren) =>
    changeTransferDestination({pot, transferDestinationFieldHelpers});

  const changeSourceId = updateTransferSourceIdFactory({
    transferSourcesFieldObj,
    sourcePots,
    transferSourcesFieldHelpers,
    ovrecommendid: builderDetails?.id || false,
  });

  useResetTransferSourcesOnPlanTypeChange({transferSourcesFieldHelpers});
  useResetDestinationIfInSources({
    checkedListSources,
    transferDestinationFieldObj,
    transferDestinationFieldHelpers,
  });

  const showAllPensions = useMemo(() => {
    if (mainPlanType !== 'pension') {
      return true;
    }
    return values.transfer?.isDependantsPension || values.transfer?.isPensionSharing;
  }, [
    values.transfer?.isDependantsPension || values.transfer?.isPensionSharing,
    mainPlanType,
  ]);

  const sourcePotsFiltered = useMemo(
    () =>
      transferSourcePots({
        sourcePots,
        builderDetails,
        mainPlanType,
        contact: values.contact,
        showAllPensions,
      }),
    [sourcePots, builderDetails, mainPlanType, values.contact, showAllPensions]
  );

  const shellPlansList = useMemo(
    () => transferShellPlans({transferSources: transferSourcesFieldMeta.value}),
    [transferSourcesFieldMeta.value]
  );

  const destPotsFiltered = useMemo(
    () =>
      transferDestinationPots({
        transferSourcesFieldObj,
        sourcePots,
        builderDetails,
        mainPlanType,
        isSppDa: accountType === BusinessUnitEnum.DA,
        showAllPensions,
        contact: values.contact,
      }),
    [
      transferSourcesFieldObj,
      sourcePots,
      builderDetails,
      mainPlanType,
      accountType,
      showAllPensions,
      values.contact,
    ]
  );

  if (!values.mainPlanType) {
    return null;
  }

  return (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={transferSourcesFieldMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {sourcePotsFiltered.map((pot) => (
            <TransferSourceParent
              key={pot.id}
              pot={pot}
              selectedSourceIds={checkedListSources.total}
              greyed={
                pot.id === destinationExistingPotId || pot.id === destinationParentPotId
              }
              sameReview={pot.review?.id === builderReviewId}
              handleChange={changeSourceId}
            />
          ))}
          {shellPlansList.map((shellPlan, index) => (
            <BuilderTransferShellPlanForm
              key={shellPlan.id}
              shellPlan={shellPlan}
              index={index}
              isSelected={checkedListSources.total.includes(shellPlan.id) || false}
              handleChange={changeSourceId}
            />
          ))}
        </BuilderPlanFormsStyled>
        {isNonEditable ? null : (
          <Box
            mt={4}
            pb={12}
            ml={2}
            display="flex"
            alignContent="center"
            justifyContent="center"
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                addShellSourcePlan({
                  checkedListSources,
                  setFieldValue,
                  shellProducts: shellProducts || [],
                  mainPlanTypeValue: mainPlanType,
                });
              }}
            >
              Add{' '}
              {sourcePotsFiltered.filter((x) => x.id === CASH_OBJ.id).length
                ? 'Another'
                : ''}{' '}
              New Source Plan
            </Button>
          </Box>
        )}
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={
            transferDestinationFieldMeta.error
              ? typeof transferDestinationFieldMeta.error === 'string'
                ? transferDestinationFieldMeta.error
                : (transferDestinationFieldMeta.error as {existingPotId?: string})
                    ?.existingPotId
              : undefined
          }
          destinationLabel={sourceDestinationLabels?.second}
        />
        <BuilderPlanFormsStyled>
          {destPotsFiltered.map((pot) => (
            <TransferDestinationParent
              key={pot.id}
              pot={pot}
              destinationExistingPotId={destinationExistingPotId}
              disabled={isNonEditable}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={changeDestination}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
